import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Body, Button, ButtonGroup, H2 } from "@datapay-by-datacom/design-system";
import { useCompanyMappings } from "../hooks/useCompanyMappings";
import { useComponentsParameters } from "../hooks/useComponentsParameters";
import { useMapColumns } from "../hooks/useMapColumns";
import {
  mappingSelectionKey,
  useMappingElements,
  type MappingLayoutMode,
} from "../hooks/useMappingElements";
import { useSaveMappings } from "../hooks/useSaveMappings";
import { useWizard } from "../hooks/useWizard";
import { PAY_COMPONENT_IMPORT_BASE_PATH } from "../config";
import type { ComponentParameter } from "../types";
import {
  deriveComponentInstances,
  type ComponentInstances,
} from "../utils/componentAmbiguity";
import { distinctComponentNames } from "../utils/componentNames";
import {
  detectEndDateColumn,
  detectStartDateColumn,
} from "../utils/dateColumns";
import { RESERVED_LC, resolveParam } from "../utils/mappedRows";
import { formatMissingMappingsMessage } from "../utils/mappingValidation";
import { formatUserFacingError } from "../utils/apiErrorMessage";
import { AssignmentDateColumnsFields } from "../components/AssignmentDateColumnsFields";
import { MappingTablesPanel } from "../components/MappingTablesPanel";
import styles from "./MapColumnsScreen.module.css";

/**
 * Map Columns screen — always shown after upload.
 *
 * Discovers every component in the CSV, groups them by pay element, and renders
 * a mapping table per element where the PS user assigns each CSV data column to
 * a parameter (or skips it). Any mappings already saved for the company are
 * pre-filled so the user can reuse or adjust them — the step is never skipped.
 *
 * On "Save and Continue":
 * 1. Builds `MappingEntry[]` from current selections (skipping "— skip —" choices).
 * 2. Persists via PUT `/pay-components/mappings`.
 * 3. Runs a dry-run import → the Review screen.
 *
 * NOTE: By default, components on the same pay element share one mapping table.
 * A toggle lets the user map those siblings individually when needed.
 */
export function MapColumnsScreen() {
  const { companyCode, state, updateState, runImport, invalidateValidation } =
    useWizard();
  const navigate = useNavigate();

  const [mappingMode, setMappingMode] = useState<MappingLayoutMode>("grouped");

  const preview = state.preview;
  const headers = useMemo(() => preview?.headers ?? [], [preview]);
  const rows = useMemo(() => preview?.rows ?? [], [preview]);

  const activeComponentColumn =
    state.componentColumn ?? preview?.componentColumn ?? "";
  const activeStartDateColumn = state.startDateColumn ?? "";
  const activeEndDateColumn = state.endDateColumn ?? "";

  // Pre-select reserved Start Date / End Date headers when present (decision 2A).
  useEffect(() => {
    if (!preview) return;
    const updates: {
      startDateColumn?: string;
      endDateColumn?: string;
    } = {};
    if (state.startDateColumn == null) {
      const detected = detectStartDateColumn(headers);
      if (detected) updates.startDateColumn = detected;
    }
    if (state.endDateColumn == null) {
      const detected = detectEndDateColumn(headers);
      if (detected) updates.endDateColumn = detected;
    }
    if (Object.keys(updates).length > 0) {
      updateState(updates);
    }
  }, [
    preview,
    headers,
    state.startDateColumn,
    state.endDateColumn,
    updateState,
  ]);

  // Distinct component names from the active component column (drives discovery).
  const componentNames = useMemo(
    () => distinctComponentNames(rows, activeComponentColumn),
    [rows, activeComponentColumn],
  );

  const paramsQuery = useComponentsParameters(
    companyCode,
    componentNames,
    state.mypayEnvironment,
  );
  const mappingsQuery = useCompanyMappings(companyCode);

  // Data columns = CSV headers minus reserved/informational, the component
  // column, and any mapped assignment-date columns.
  const dataColumns = useMemo(
    () =>
      headers.filter(
        (h) =>
          !RESERVED_LC.has(h.toLowerCase()) &&
          h !== activeComponentColumn &&
          h !== activeStartDateColumn &&
          h !== activeEndDateColumn,
      ),
    [
      headers,
      activeComponentColumn,
      activeStartDateColumn,
      activeEndDateColumn,
    ],
  );

  const { elements, componentNamesByElement, unknownComponents, hasSharedElements } =
    useMappingElements(componentNames, paramsQuery.data ?? {}, mappingMode);

  // Ambiguous component names present in this CSV (D4). Discovery flags a name as
  // ambiguous when it maps to more than one company component; the PS user must
  // pick which instance to import before continuing.
  const componentInstances = useMemo(
    () => deriveComponentInstances(paramsQuery.data ?? {}),
    [paramsQuery.data],
  );
  const resolutions = state.componentResolutions ?? {};
  const ambiguousComponents = useMemo(
    () =>
      componentNames
        .map((name) => ({ name, instances: componentInstances[name] }))
        .filter(
          (c): c is { name: string; instances: ComponentInstances } =>
            c.instances?.isAmbiguous === true && c.instances.options.length > 0,
        ),
    [componentNames, componentInstances],
  );
  const hasUnresolvedAmbiguity = ambiguousComponents.some(
    ({ name }) => !resolutions[name],
  );

  const handleSelectInstance = (name: string, companyComponentId: string) => {
    const next = { ...(state.componentResolutions ?? {}) };
    if (companyComponentId === "") {
      delete next[name];
    } else {
      next[name] = companyComponentId;
    }
    updateState({ componentResolutions: next });
  };

  // Pre-fill selections from the company's saved mappings.
  const initialSelections = useMemo(() => {
    const mappings = mappingsQuery.data ?? [];
    const sel: Record<string, Record<string, ComponentParameter>> = {};
    for (const element of elements) {
      const inner: Record<string, ComponentParameter> = {};
      const scopeKey = mappingSelectionKey(element, mappingMode);

      if (mappingMode === "grouped") {
        for (const m of mappings) {
          if (
            m.componentName ||
            m.elementQualifiedName !== element.elementQualifiedName
          ) {
            continue;
          }
          const param = resolveParam(element.parameters, m);
          if (param) inner[m.columnName] = param;
        }
      } else {
        const componentMappings = mappings.filter(
          (m) =>
            m.componentName === element.componentCode &&
            m.elementQualifiedName === element.elementQualifiedName,
        );
        const legacyMappings = mappings.filter(
          (m) =>
            !m.componentName &&
            m.elementQualifiedName === element.elementQualifiedName,
        );
        const applicable =
          componentMappings.length > 0 ? componentMappings : legacyMappings;
        for (const m of applicable) {
          const param = resolveParam(element.parameters, m);
          if (param) inner[m.columnName] = param;
        }
      }

      if (Object.keys(inner).length > 0) {
        sel[scopeKey] = inner;
      }
    }
    return sel;
  }, [elements, mappingsQuery.data, mappingMode]);

  const { selections, setSelection, isReady, elementsMissingMappings, buildMappingsSavePayload } =
    useMapColumns(elements, dataColumns, mappingMode, initialSelections);

  const saveMappings = useSaveMappings(companyCode);

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [mappingValidationError, setMappingValidationError] = useState<
    string | null
  >(null);
  const [highlightUnmappedScopes, setHighlightUnmappedScopes] = useState(false);

  const isLoading = paramsQuery.isLoading || mappingsQuery.isLoading;
  const paramsLoadError = paramsQuery.error;
  const mappingsLoadError = mappingsQuery.error;

  const handleSaveAndContinue = async () => {
    setIsSaving(true);
    setSaveError(null);
    setMappingValidationError(null);
    setHighlightUnmappedScopes(false);

    if (hasUnresolvedAmbiguity) {
      setMappingValidationError(
        "Choose which component instance to import for each ambiguous name before continuing.",
      );
      setIsSaving(false);
      return;
    }

    if (dataColumns.length > 0 && elementsMissingMappings.length > 0) {
      setMappingValidationError(
        formatMissingMappingsMessage(
          elementsMissingMappings,
          mappingMode,
          componentNamesByElement,
        ),
      );
      setHighlightUnmappedScopes(true);
      setIsSaving(false);
      return;
    }

    try {
      const payload = buildMappingsSavePayload();
      await saveMappings.mutateAsync(payload);
      await runImport(true); // dry-run → review screen before committing
    } catch (err) {
      setSaveError(formatUserFacingError(err));
    } finally {
      setIsSaving(false);
    }
  };

  if (!preview) {
    return (
      <div className={styles.container}>
        <H2>Map Columns</H2>
        <Body>
          No CSV to map. Upload a file on the previous step — refreshing this
          page clears the in-progress import.
        </Body>
        <ButtonGroup align="right">
          <Button
            variant="primary"
            onPress={() => navigate(PAY_COMPONENT_IMPORT_BASE_PATH)}
          >
            Back to upload
          </Button>
        </ButtonGroup>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={styles.container}>
        <H2>Map Columns</H2>
        <Body>Loading components and saved mappings…</Body>
      </div>
    );
  }

  if (paramsLoadError) {
    return (
      <div className={styles.container}>
        <H2>Map Columns</H2>
        <div className={styles.errorBanner} role="alert">
          <p>
            <strong>Could not load components:</strong>{" "}
            {formatUserFacingError(paramsLoadError)}
          </p>
          {mappingsLoadError && (
            <p>
              <strong>Could not load saved mappings:</strong>{" "}
              {formatUserFacingError(mappingsLoadError)}
            </p>
          )}
        </div>
        <ButtonGroup align="right">
          <Button
            variant="primary"
            onPress={() => navigate(PAY_COMPONENT_IMPORT_BASE_PATH)}
          >
            Back to upload
          </Button>
        </ButtonGroup>
      </div>
    );
  }

  if (elements.length === 0) {
    return (
      <div className={styles.container}>
        <H2>Map Columns</H2>
        <Body>
          No known components were found in this file. Check that the correct
          component column is selected on the upload step.
        </Body>
        {unknownComponents.length > 0 && (
          <div className={styles.warningBanner} role="alert">
            <strong>Unknown components:</strong> {unknownComponents.join(", ")}.
            These names don't match any component for this company and will be
            rejected on import.
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <H2>Map Columns</H2>
        {mappingsLoadError && (
          <div className={styles.warningBanner} role="alert">
            <strong>Could not load saved mappings:</strong>{" "}
            {formatUserFacingError(mappingsLoadError)} You can still map columns
            manually below.
          </div>
        )}
        <Body>
          Map each CSV column to its corresponding pay element parameter for
          each component below. Existing mappings are pre-filled — adjust as
          needed. Choose <em>— skip —</em> to leave a column unmapped.
        </Body>
      </div>

      <AssignmentDateColumnsFields
        headers={headers}
        startDateColumn={activeStartDateColumn}
        endDateColumn={activeEndDateColumn}
        componentColumn={activeComponentColumn}
        disabled={isSaving}
        onStartDateColumnChange={(column) => {
          updateState({ startDateColumn: column });
          invalidateValidation();
        }}
        onEndDateColumnChange={(column) => {
          updateState({ endDateColumn: column });
          invalidateValidation();
        }}
      />

      <MappingTablesPanel
        mappingMode={mappingMode}
        onMappingModeChange={setMappingMode}
        hasSharedElements={hasSharedElements}
        controlsDisabled={isSaving}
        unknownComponents={unknownComponents}
        ambiguousComponents={ambiguousComponents}
        resolutions={resolutions}
        onSelectInstance={handleSelectInstance}
        elements={elements}
        componentNamesByElement={componentNamesByElement}
        dataColumns={dataColumns}
        selections={selections}
        setSelection={setSelection}
        highlightUnmappedScopes={highlightUnmappedScopes}
        elementsMissingMappings={elementsMissingMappings}
        onMappingFieldChange={() => {
          setMappingValidationError(null);
          setHighlightUnmappedScopes(false);
        }}
      />

      {mappingValidationError && (
        <div className={styles.errorBanner} role="alert">
          <p>
            <strong>Mapping required:</strong> {mappingValidationError}
          </p>
        </div>
      )}

      {saveError && (
        <div className={styles.errorBanner} role="alert">
          <p>
            <strong>Save failed:</strong> {saveError}
          </p>
        </div>
      )}

      <ButtonGroup align="right">
        <Button
          variant="tertiary"
          isDisabled={isSaving}
          onPress={() => navigate(PAY_COMPONENT_IMPORT_BASE_PATH)}
        >
          Back to upload
        </Button>
        <Button
          variant="primary"
          isDisabled={!isReady || isSaving}
          onPress={handleSaveAndContinue}
        >
          {isSaving ? "Saving…" : "Save and Continue"}
        </Button>
      </ButtonGroup>
    </div>
  );
}
